import { useRef, useState, type DragEvent } from "react";
import { UploadCloud } from "lucide-react";
import { type RecordData } from "./Workflow";
import { periodText } from "./PortalPerformance";
import "./PortalReportUpload.css";

type Send = (path: string, body?: RecordData | FormData, method?: string) => Promise<any>;
const SHEET = /\.(xlsx|xls|csv)$/i;
// Vercel refuses a body over 4.5 MB before the API sees it.
const MAX_BYTES = 4 * 1024 * 1024;

function Drop({ label, hint, file, onFile, disabled }: { label: string; hint: string; file: File | null; onFile: (f: File | null) => void; disabled: boolean }) {
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  function drop(e: DragEvent<HTMLLabelElement>) { e.preventDefault(); setOver(false); onFile(e.dataTransfer.files?.[0] || null); }
  return (
    <label className={`report-drop${over ? " is-over" : ""}`} onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={drop}>
      <UploadCloud aria-hidden="true" />
      <strong>{file ? file.name : label}</strong>
      <small>{hint}</small>
      <input ref={input} type="file" accept=".xlsx,.xls,.csv" aria-label={label} disabled={disabled} onChange={(e) => onFile(e.target.files?.[0] || null)} />
    </label>
  );
}

/**
 * The owner sends their own call center export from the portal (Neil, 2026-11-03),
 * the same upload the agency runs on Call center reports. NOLA reads the period
 * from the file and the new report shows in the list below.
 */
/** onDone gets the thank-you line: the page reloads its report list, which redraws this card. */
export default function PortalCallUpload({ clientId, send, onDone }: { clientId: number; send: Send; onDone: (message: string) => void }) {
  const [calls, setCalls] = useState<File | null>(null), [appointments, setAppointments] = useState<File | null>(null);
  const [busy, setBusy] = useState(false), [error, setError] = useState("");
  const [round, setRound] = useState(0);
  function pick(set: (f: File | null) => void) {
    return (next: File | null) => {
      if (!next) return;
      if (!SHEET.test(next.name)) { setError("Choose the .xlsx or .csv file as it came out of your system."); return; }
      setError("");
      set(next);
    };
  }
  async function upload() {
    if (!calls || busy) return;
    if (calls.size + (appointments?.size || 0) > MAX_BYTES) { setError("These files add up to more than 4 MB. Export a shorter period and send each part."); return; }
    setBusy(true); setError("");
    const body = new FormData();
    body.set("client_id", String(clientId));
    body.append("call_center_csv", calls);
    if (appointments) body.append("appointment_csv", appointments);
    try {
      const r = await send("/api/callcenter/upload", body, "POST");
      setCalls(null); setAppointments(null); setRound((n) => n + 1);
      onDone(`Got it. Your report for ${r?.period?.start ? periodText(r.period.start, r.period.end) : "this period"} is in the list below. The written summary takes a minute or two.`);
    } catch (e) {
      const message = (e as Error).message || "The upload did not go through.";
      setError(/already exists/i.test(message) ? "A report for this period is already here. Export a different period, or ask your agency to replace it." : message);
    } finally { setBusy(false); }
  }
  return (
    <section className="panel workflow-panel portal-report-upload portal-call-upload" aria-labelledby="portal-call-upload-heading">
      <h2 id="portal-call-upload-heading">Upload calls</h2>
      <p className="workflow-note">Export your calls from the call center system and drop the file here. Add the appointment export too and the report shows booked revenue.</p>
      <div className="portal-call-drops" key={round}>
        <Drop label="Call center export" hint="Required. .xlsx or .csv" file={calls} onFile={pick(setCalls)} disabled={busy} />
        <Drop label="Appointment export" hint="Optional. .xlsx or .csv" file={appointments} onFile={pick(setAppointments)} disabled={busy} />
      </div>
      <div className="report-actions">
        <button type="button" className="button" disabled={!calls || busy} onClick={() => void upload()}>{busy ? "Reading your calls…" : "Upload calls"}</button>
      </div>
      {error && <p className="inline-notice danger" role="alert">{error}</p>}
    </section>
  );
}
